import { type FC, type FormEvent, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  Grid,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import {
  useCollectionOptionsQuery,
  useCreateContentMutation,
  useUpdateContentManyMutation,
} from "@/partner/entities/contents";
import { ContentTypeType } from "@/types/graphql/partner.generated";

// Трёхсостояние чекбокса коллекции относительно выбранных элементов
// (порт референсных FULL/PART/NONE)
const FULL = "FULL";
const PART = "PART";
const NONE = "NONE";
type CheckboxState = typeof FULL | typeof PART | typeof NONE;

export interface AddToCollectionItem {
  id: string;
  collectionIds: string[];
}

interface AddToCollectionButtonProps {
  selectedItems: AddToCollectionItem[];
  onCompleted: () => void;
  disabled?: boolean;
  /** Рендер как пункт меню «Действия» (иначе — кнопка) */
  menuItem?: boolean;
  /** Списки, которые нужно рефетчить после привязки (фикс референсного
   * захардкоженного ["Videos"]) */
  refetchQueries: string[];
}

// Порт референсного AddToCollectionButton: меню 400×400 со списком
// чекбоксов коллекций (FULL/PART/NONE по выбранным элементам) и инлайн-
// созданием коллекции; merge-логика сохранения — PART остаются, FULL
// добавляются.
export const AddToCollectionButton: FC<AddToCollectionButtonProps> = ({
  selectedItems,
  onCompleted,
  disabled = false,
  menuItem = false,
  refetchQueries,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [createMode, setCreateMode] = useState(false);
  const [title, setTitle] = useState("");
  const [checkboxState, setCheckboxState] = useState<
    Record<string, CheckboxState>
  >({});

  const { data, refetch } = useCollectionOptionsQuery({
    skip: !open,
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first",
    variables: {
      page: 0,
      rowsPerPage: 1000,
      filter: { type: ContentTypeType.CollectionType },
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });
  const collections = useMemo(
    () => data?.contents?.nodes ?? [],
    [data?.contents?.nodes]
  );

  // Пересчёт трёхсостояния при смене выборки/списка (порт useEffect)
  useEffect(() => {
    setCheckboxState(() =>
      collections.reduce<Record<string, CheckboxState>>((all, collection) => {
        const full = selectedItems.every((item) =>
          (item.collectionIds ?? []).includes(collection.id)
        );
        const part = selectedItems.some((item) =>
          (item.collectionIds ?? []).includes(collection.id)
        );
        all[collection.id] = full ? FULL : part ? PART : NONE;
        return all;
      }, {})
    );
  }, [selectedItems, collections]);

  const [createCollection, { loading: creating }] = useCreateContentMutation({
    onCompleted: () => {
      setTitle("");
      setCreateMode(false);
      enqueueSnackbar(t("collections.addButton.collectionCreated"), {
        variant: "success",
      });
      void refetch();
    },
    onError: (error) => {
      enqueueSnackbar(error.message, { variant: "error" });
    },
  });

  const [updateContentMany, { loading: saving }] = useUpdateContentManyMutation(
    {
      refetchQueries,
      onCompleted: () => {
        setAnchorEl(null);
        enqueueSnackbar(t("collections.addButton.contentsUpdated"), {
          variant: "success",
        });
        onCompleted();
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: "error" });
      },
    }
  );

  const onClickListItem = (collectionId: string) => {
    setCheckboxState((prev) => ({
      ...prev,
      // FULL → NONE, PART/NONE → FULL (порт клика по строке)
      [collectionId]: prev[collectionId] === FULL ? NONE : FULL,
    }));
  };

  const onClickSave = () => {
    // Merge-логика референса: частично отмеченные (PART) элементы сохраняют
    // свои привязки, FULL добавляется всем, снятые (NONE) — убираются.
    const input = selectedItems.map((item) => {
      const collectionIds = (item.collectionIds ?? [])
        .filter((collectionId) => checkboxState[collectionId] === PART)
        .concat(
          Object.keys(checkboxState).filter(
            (collectionId) => checkboxState[collectionId] === FULL
          )
        );
      return { id: item.id, collectionIds };
    });
    updateContentMany({ variables: { input } });
  };

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    event.stopPropagation();
    createCollection({
      variables: { input: { title, type: ContentTypeType.CollectionType } },
    });
  };

  const onReset = () => {
    setTitle("");
    setCreateMode(false);
  };

  const trigger = menuItem ? (
    <MenuItem
      onClick={(event) => setAnchorEl(event.currentTarget)}
      disabled={disabled}
    >
      {t("collections.addButton.button")}
    </MenuItem>
  ) : (
    <Button
      color="inherit"
      onClick={(event) => setAnchorEl(event.currentTarget)}
      disabled={disabled}
    >
      {t("collections.addButton.button")}
    </Button>
  );

  return (
    <>
      {trigger}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{ paper: { sx: { width: 400, maxWidth: "100%" } } }}
      >
        <form onSubmit={onSubmit} onReset={onReset}>
          <Grid
            container
            sx={{ px: 2, py: 1, bgcolor: "background.default" }}
            alignItems="center"
          >
            {createMode ? (
              <>
                <Grid size={7}>
                  <Button type="reset" variant="outlined" size="small">
                    {t("common.actions.cancel")}
                  </Button>
                </Grid>
                <Grid size={5} sx={{ textAlign: "right" }}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                    disabled={creating || !title.trim()}
                  >
                    {t("common.actions.create")}
                  </Button>
                </Grid>
              </>
            ) : (
              <>
                <Grid size={7}>
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => setCreateMode(true)}
                  >
                    {t("collections.addButton.createCollection")}
                  </Button>
                </Grid>
                <Grid size={5} sx={{ textAlign: "right" }}>
                  <Button
                    type="button"
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={onClickSave}
                    disabled={saving}
                  >
                    {t("common.actions.save")}
                  </Button>
                </Grid>
              </>
            )}
          </Grid>

          {createMode && (
            <>
              <Box sx={{ px: 2, py: 1 }}>
                <TextField
                  autoFocus
                  required
                  fullWidth
                  size="small"
                  label={t("collections.addButton.titleLabel")}
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                />
              </Box>
              <Divider />
            </>
          )}

          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              listStyle: "none",
              overflowY: "auto",
              height: createMode ? 258 : 347,
            }}
          >
            {collections.map((collection) => {
              const state = checkboxState[collection.id] ?? NONE;
              return (
                <ListItemButton
                  key={collection.id}
                  dense
                  onClick={() => onClickListItem(collection.id)}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      checked={state === FULL}
                      indeterminate={state === PART}
                      tabIndex={-1}
                      color="default"
                      disableRipple
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={collection.title}
                    primaryTypographyProps={{
                      color: state === NONE ? "textSecondary" : "textPrimary",
                    }}
                  />
                </ListItemButton>
              );
            })}
          </Box>
        </form>
      </Menu>
    </>
  );
};
